import { Platform } from '@angular/cdk/platform';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Observable, ReplaySubject } from 'rxjs';

import { MtnaLogger } from '@mtna/lib-ui';
import { OptionBundle, OptionSet, PackagingOptions, ProcessStatusDetail } from '@mtna/rds-packaging-ui';

import {
  SelectParameters,
  serializeSelectOptionsUrlParams,
  serializeTabulationOptionsUrlParams,
  TabulationParameters,
} from '../models/index';
import { RdsApiService } from './rds-api.service';

@Injectable({
  providedIn: 'root'
})
export class RdsPackagingService {
  get PACKAGE_URL(): string {
    return `${this.apiService.getApiUrl()}/package`;
  }

  get DATA_PRODUCT_URL(): string {
    return `${this.PACKAGE_URL}/${this.apiService.getCatalogId()}/${this.apiService.getDataProductId()}`;
  }

  protected _bundlesCache$ = new ReplaySubject<OptionBundle[]>(1);
  protected _bundlesRequest$: Observable<OptionBundle[]>;
  protected _optionsCache$ = new ReplaySubject<PackagingOptions>(1);
  protected _optionsRequest$: Observable<PackagingOptions>;

  constructor(protected apiService: RdsApiService, private http: HttpClient, protected logger: MtnaLogger, protected platform: Platform) {}

  downloadPackagedFile(processId: string): void {
    const a = document.createElement('a');
    a.setAttribute('href', `${this.PACKAGE_URL}/${processId}`);
    a.setAttribute('target', '_blank');
    document.body.appendChild(a);
    this.logger.log('[@mtna/rds-lib-ui] RdsPackagingService#downloadPackagedFile - generated anchor:', a);
    a.click();

    if (this.platform.SAFARI) {
      this.logger.log('Safari detected, using window.location.href to download file.');
      window.location.href = a.href;
    }
    a.parentElement.removeChild(a);
  }

  /**
   * Get bundle. When a client wants to get a particular bundle they can use this endpoint
   * to retrieve a list of OptionSets that are pre-configured to work with each other.
   * @param bundleId The ID of the bundle whose options are desired
   */
  getBundle(bundleId: string): Observable<OptionSet[]> {
    return this.http.get<OptionSet[]>(`${this.PACKAGE_URL}/option/bundle/${bundleId}`);
  }

  /**
   * Get all packaging bundles.
   * These are groups of related OptionSets grouped together with some pre-configuration already done.
   * This can ensure that users can get a set of options quickly and already set up with all the options needed.
   * @remarks This method uses caching, returns a hot observable.
   * @param refresh forces a cache refresh, will update all subscribers. Defaults to false.
   * @returns all packaging bundles
   */
  getBundles(refresh: boolean = false): Observable<OptionBundle[]> {
    if (refresh || !this._bundlesRequest$) {
      this.logger.debug(`[RdsPackagingService] getBundles - Fetching, ${refresh ? 'forced refresh' : 'not in cache'}`);
      this._bundlesRequest$ = this.http.get<OptionBundle[]>(`${this.PACKAGE_URL}/option/bundles`);
      this._bundlesRequest$.subscribe(result => this._bundlesCache$.next(result), err => this._bundlesCache$.error(err));
    }
    return this._bundlesCache$.asObservable();
  }

  getOption(formatId: string): Observable<OptionSet> {
    return this.http.get<OptionSet>(`${this.PACKAGE_URL}/options/${formatId}`);
  }

  /**
   * Get all packaging options.
   * This is a starting point for clients to identify all the possible packaging formats that are available.
   * @remarks This method uses caching, returns a hot observable.
   * @param refresh forces a cache refresh, will update all subscribers. Defaults to false.
   * @returns all packaging options.
   */
  getOptions(refresh: boolean = false): Observable<PackagingOptions> {
    if (refresh || !this._optionsRequest$) {
      this.logger.debug(`[RdsPackagingService] getOptions - Fetching, ${refresh ? 'forced refresh' : 'not in cache'}`);
      this._optionsRequest$ = this.http.get<PackagingOptions>(`${this.PACKAGE_URL}/options`);
      this._optionsRequest$.subscribe(result => this._optionsCache$.next(result), err => this._optionsCache$.error(err));
    }
    return this._optionsCache$.asObservable();
  }

  getProcessDetails(processId: string): Observable<ProcessStatusDetail> {
    return this.http.get<ProcessStatusDetail>(`${this.PACKAGE_URL}/processdetails/${processId}`);
  }

  getPackagedFile(processId: string): Observable<HttpResponse<Blob>> {
    return this.http.get(`${this.PACKAGE_URL}/${processId}`, {
      observe: 'response',
      responseType: 'blob'
    });
  }

  select(params: SelectParameters, optionSets: OptionSet[]): Observable<string> {
    const paramsPath = serializeSelectOptionsUrlParams(params);
    return this.http.post<string>(`${this.DATA_PRODUCT_URL}/select?${paramsPath}`, optionSets);
  }

  /**
   * Package a tabulation.
   *
   * @param optionSets packaging format option sets
   * @param options various tabulation parameters
   * @returns packaging ID
   */
  tabulate(params: TabulationParameters, optionSets: Array<OptionSet>): Observable<string> {
    const paramsPath = serializeTabulationOptionsUrlParams(params);
    return this.http.post<string>(`${this.DATA_PRODUCT_URL}/tabulate?${paramsPath}`, optionSets);
  }
}
